import React from 'react';
import { Card, Radio, Row, Col, Spin } from 'syngenta-digital-cropwise-react-ui-kit';
import { Product } from '../types';
import { useTranslation } from 'react-i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  evaluateBooleanFeatureFlag,
  LDFeatureFlagEnum,
} from 'tools/feature-flags/launchdarkly-flags';
import { APIState } from '../../../models/APIState';

type UnmappedResourceListProps = {
  unmappedResources: Product[];
  status: string;
  onChange: any;
  value: any;
};

export default function UnmappedResourceList({
  unmappedResources,
  status,
  value,
  onChange,
}: UnmappedResourceListProps) {
  const getItemName = (product: Product) => product.description || product.product_id;
  const flags = useFlags();
  const { t } = useTranslation();

  const showOperationCount = evaluateBooleanFeatureFlag(
    flags,
    LDFeatureFlagEnum.SHOW_UNMAPPED_OPERATION_COUNT,
  );

  const radioItems =
    unmappedResources?.map((product: Product) => (
      <label
        htmlFor={product.product_id}
        key={product.product_id}
        data-cy={`${product.product_id}-product`}
        className="block cursor-pointer"
      >
        <Card
          className={`unmapped-resource-card shadow-sm-card mb-8 rounded-12 w-100% h-72px text-left overflow-hidden${
            showOperationCount ? '' : ' hide-operation-count'
          }`}
          data-cy={`${product.product_id}-product`}
        >
          <Row className="flex-nowrap gap-2">
            <Col className="flex items-center">
              <Radio
                value={product.product_id}
                data-cy={product.product_id + '_radio'}
                id={product.product_id}
              />
            </Col>
            <Col data-cy={product.product_id + '_instances'} className="pr-8">
              <Row>
                <span className="text-neutral-100 whitespace-nowrap overflow-ellipsis overflow-hidden font-semibold">
                  {getItemName(product)}
                </span>
              </Row>
              {showOperationCount && (
                <Row>
                  {product?.total_instance_count ?? 0} {t('resource-mapper.instance')}
                  {product?.total_instance_count === 1 ? '' : 's'}
                </Row>
              )}
            </Col>
          </Row>
        </Card>
      </label>
    )) ?? [];

  switch (status) {
    case APIState.SUCCESS:
      return (
        <div className="py-8 px-2">
          <Radio.Group
            data-cy="unmapped_resource_list"
            onChange={onChange}
            value={value}
            className="w-100% text-center"
          >
            {radioItems}
          </Radio.Group>
        </div>
      );
    case APIState.LOADING:
      return (
        <div data-cy="unmapped_resource_list_loading" className="h-100% w-100% text-center mt-40">
          <Spin className="syt-default" delay={500} size="default" spinning />
        </div>
      );
    case APIState.FAIL:
      return (
        <div data-cy="unmapped_resource_list_error" className="h-100% w-100% text-center mt-40">
          <p>{t('resource-mapper.integration_error')}</p>
        </div>
      );
    case APIState.EMPTY:
    default:
      return (
        <div className="h-100% w-100% text-center mt-40">
          <p>{t('resource-mapper.no_unmapped_resources')}</p>
        </div>
      );
  }
}
